import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { axiosClient } from '../../../core/axios-client'

const createCompany = createAsyncThunk('createCompany', async (payload, { rejectWithValue }) => {
  try {
    const response = await axiosClient.post('/companies/', payload)
    return response.data
  } catch (err) {
    return rejectWithValue(err.response?.data || err.message)
  }
})

const updateCompany = createAsyncThunk('updateCompany', async (payload, { rejectWithValue }) => {
  try {
    const id = payload?.id
    if (!id) return rejectWithValue('Company id is required')
    const { id: _id, ...data } = payload
    const response = await axiosClient.patch(`/companies/${id}`, data)
    return response.data
  } catch (err) {
    return rejectWithValue(err.response?.data || err.message)
  }
})

const fetchCompanies = createAsyncThunk('fetchCompanies', async ({ filters = {} } = {}) => {
  const response = await axiosClient.get('/companies/', {
    params: { ...filters },
  })
  return response.data
})

const fetchSupplierCompanies = createAsyncThunk('fetchSupplierCompanies', async () => {
  const response = await axiosClient.get('/companies/?is_supplier=true')
  return response.data.results
})

const fetchSelectedCompany = createAsyncThunk('fetchSelectedCompany', async () => {
  const response = await axiosClient.get('/companies/selected')
  return response.data
})

const setSelectedCompany = createAsyncThunk('setSelectedCompany', async ({ id }) => {
  const response = await axiosClient.put(`/companies/selected/${id}`)
  return response.data.results
})

const createCourierInvitation = createAsyncThunk(
  'createCourierInvitation',
  async ({ company_id }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/courier-invitations/', { company_id })
      return response.data
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

const initialState = {
  companies: [],
  supplier_companies: [],
  selected_company: null,
  needsOnboarding: false,

  listItems: {
    items: [],
    page: 1,
    page_size: 20,
    count: 0,
  },

  updateStatus: 'idle',
  updateError: null,
}

export const slice = createSlice({
  name: 'companies',
  initialState,

  reducers: {
    setListItems(state, { payload }) {
      state.listItems = {
        ...state.listItems,
        ...payload,
      }
    },
    resetListItems(state) {
      state.listItems = { items: [], page: 1, page_size: 20, count: 0 }
    },
    resetUpdateState(state) {
      state.updateStatus = 'idle'
      state.updateError = null
    },
  },

  extraReducers: (builder) => {
    builder.addCase(fetchCompanies.fulfilled, (state, { payload }) => {
      const { results = [], count = 0, page = state.listItems.page, page_size = state.listItems.page_size } = payload || {}

      state.companies = results
      state.needsOnboarding = results.length === 0

      state.listItems = {
        ...state.listItems,
        items: results,
        count,
        page,
        page_size,
      }
    })

    builder.addCase(fetchSupplierCompanies.fulfilled, (state, { payload }) => {
      state.supplier_companies = payload
    })

    builder.addCase(fetchSelectedCompany.fulfilled, (state, { payload }) => {
      state.selected_company = payload
    })

    builder.addCase(setSelectedCompany.fulfilled, (state, { payload }) => {
      state.selected_company = payload
    })

    builder.addCase(updateCompany.pending, (state) => {
      state.updateStatus = 'loading'
      state.updateError = null
    })
    builder.addCase(updateCompany.fulfilled, (state, { payload }) => {
      state.updateStatus = 'succeeded'
      state.updateError = null

      const id = String(payload?.id ?? '')
      if (id && Array.isArray(state.listItems?.items)) {
        const idx = state.listItems.items.findIndex((c) => String(c?.id) === id)
        if (idx >= 0) state.listItems.items[idx] = { ...state.listItems.items[idx], ...payload }
      }

      if (state.selected_company && String(state.selected_company?.id) === id) {
        state.selected_company = { ...state.selected_company, ...payload }
      }
    })
    builder.addCase(updateCompany.rejected, (state, { payload }) => {
      state.updateStatus = 'failed'
      state.updateError = payload || 'Failed to update company'
    })
  },
})

export const { setListItems, resetListItems, resetUpdateState } = slice.actions

export {
  createCompany,
  updateCompany,
  fetchCompanies,
  fetchSupplierCompanies,
  fetchSelectedCompany,
  setSelectedCompany,
  createCourierInvitation,
  setListItems as setCompaniesListItems,
}

export default slice.reducer